// Renders each page of a PDF File to a JPEG thumbnail object URL using a
// lazily-imported pdfjs-dist. Callers must revoke the returned URLs when done.
const THUMBNAIL_MAX_WIDTH = 220;

export const renderPdfThumbnails = async (
  pdfFile: File,
  options: {
    signal?: AbortSignal;
    /** Page width in px (default: thumbnail size). */
    maxWidth?: number;
    /** Render only the first N pages. */
    maxPages?: number;
    onPage?: (done: number, total: number) => void;
  } = {},
): Promise<string[]> => {
  const pdfjs = await import("pdfjs-dist");
  // Worker is bundled by Vite as a URL; set it once.
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = (
      await import("pdfjs-dist/build/pdf.worker.min.mjs?url")
    ).default;
  }

  const data = new Uint8Array(await pdfFile.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;
  const urls: string[] = [];

  try {
    const pages = Math.min(doc.numPages, options.maxPages ?? doc.numPages);
    for (let pageNumber = 1; pageNumber <= pages; pageNumber++) {
      if (options.signal?.aborted) break;
      const page = await doc.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale =
        (options.maxWidth ?? THUMBNAIL_MAX_WIDTH) / baseViewport.width;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not get canvas context");

      await page.render({ canvasContext: ctx, viewport }).promise;
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.8),
      );
      page.cleanup();
      // Must push exactly one url per page: callers map urls[i] -> page i+1, so a
      // silent skip would shift every later thumbnail onto the wrong page number.
      if (!blob)
        throw new Error(`Failed to render thumbnail for page ${pageNumber}`);
      urls.push(URL.createObjectURL(blob));
      options.onPage?.(pageNumber, pages);
    }
  } finally {
    await doc.destroy();
  }

  return urls;
};
